import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import FileDownload from 'js-file-download'

import { Divider, IconButton, Tooltip, useTheme } from '@material-ui/core'
import TuneIcon from '@material-ui/icons/Tune'
import AddIcon from '@material-ui/icons/Add'
import GroupAddIcon from '@material-ui/icons/GroupAdd'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { FileCopy } from '@material-ui/icons'

import {
    Container,
    DamageDrawer,
    InfrastructureFilterChips,
    MessageDrawer,
    ScheduleReportPopOver,
    SideDrawer,
    SideDrawerContainer,
} from '../../components'
import {
    useAppDispatch,
    useAptConfig,
    useAreaStatusTag,
    useBlanket,
    _useFinderSelection,
    useInspection,
    _useLease,
    useMessageDrawer,
    useOnScreen,
    _usePrunedInfrastructure,
    useRootInfrastructure,
    useUser,
    useService,
    useCompany,
    useSchedule,
} from '../../hooks'
import {
    FinderLocationSelection,
    FinderSelectionMode,
    getInspectionAnalytics,
    getInventoryInspectionList,
    getUnitInspectionList,
    GET_UNIT_INSPECTION_LIST_REQUEST,
    InspectionCompletionFilter,
    InspectionHandledFilter,
    RootState,
    setInventoryInspectionList,
    UnitInspecitonListAxiosReq,
    UnitInspectionParams,
    getStatusGroupList,
    getStatusList,
    setUnits,
} from '../../store'
import {
    ActionItem,
    BaseUnitInspection,
    Damage,
    IdentifiableObject,
    InventoryConfig,
    InventoryInspection,
    ModelListMap,
    traverse,
    Unit,
    UnitInspection,
} from '../../models'
import { AssignInspectionForm } from './AssignInspectionForm'
import { UnitInspectionDetailForm } from './UnitInspectionDetailForm'
import { InspectionAssignModal } from './InspectionAssignModal'
import { ReassignInspectionModal } from './ReassignInspectionModal'
import { FilterForm } from './FilterForm'
import { useInspectionFilter } from '../../hooks/useFilter'
import { useFilterApplied } from '../../components/Infrastructure/UseFilterApplied'
import { axiosInstance, toLocaleString } from '../../helpers'
import { InspectionAnalyticsHeadline } from './InspectionAnalyticsHeadline'
import { InspectionAnalytics } from './InspectionAnalytics'
import {
    InvInsAnalyticsBlanket,
    InventoryInspectionAnalytic,
} from './InventoryInspectionAnalyticsBlanket'
import { UnitInspectionSideDrawer } from './UnitInspectionSideDrawer'
import { VideoInspectionBlanket } from './VideoInspectionBlanket/VideoInspectionBlanket'
import { useVideoInspection } from '../../hooks/useVideoInspection'
import { typographyStyle } from '../../theme/typographyStyle'

export interface InvInsAnalyticsBlanketData {
    inventoryConfig?: InventoryConfig
    status: string
    statusId?: string
    statusCount: number
    sumPrice: number
}

export const InspectionDetail = () => {
    const { id } = useParams<{ id?: string }>()
    const dispatch = useAppDispatch()
    const theme = useTheme()

    const { analyzeWithLori } = useVideoInspection(
        Number(id) ?? -1,
        id !== undefined,
    )
    // filter state

    // drawer/ui state
    const [filterOpen, setFilterOpen] = useState(false)
    const [assignOpen, setAssignOpen] = useState(false)
    const [assignModalOpen, setAssignModalOpen] = useState(false)
    const [reassignOpen, setReassignOpen] = useState(false)
    const [messageDrawerOpen, setMessageDrawerOpen] = useState(false)
    const [detailUnitInspection, setDetailUnitInspection] = useState<
        UnitInspection | undefined
    >(undefined)
    const [
        invInsAnalyitcsBlanketOpen,
        setInvInsAnalyitcsBlanketOpen,
    ] = useState(false)

    // Video inspection blanket state
    const [videoBlanketOpen, setVideoBlanketOpen] = useState(false)
    const [
        offlineInspectionModalOpen,
        setOfflineInspectionModalOpen,
    ] = useState(false)
    const [
        videoBlanketUnitInspection,
        setVideoBlanketUnitInspection,
    ] = useState<UnitInspection | undefined>(undefined)

    const [
        invInsAnalyticsBlanketData,
        setInvInsAnalyticsBlanketData,
    ] = useState<InvInsAnalyticsBlanketData>({
        status: '',
        statusCount: 0,
        sumPrice: 0,
    })

    const [
        currentInvInsAnalytic,
        setCurrentInvInsAnalytic,
    ] = useState<InventoryInspectionAnalytic>()
    const [
        inventoryInspectionAnalyticList,
        setInventoryInspectionAnalyticList,
    ] = useState<InventoryInspectionAnalytic[]>([])
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)

    const handlePopoverClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
    }

    const [activeChannel, setActiveChannel] = useState<ActiveChannel>({
        type: null,
        modelId: null,
    })

    const popoverOpen = Boolean(anchorEl)

    const contentRef = useRef<HTMLDivElement>(null)
    const atBottomRef = useRef<HTMLDivElement>(null)
    const analyticRect = useBlanket(invInsAnalyitcsBlanketOpen, contentRef)

    // Video blanket rect - hardcoded top position below header/tabs/stats
    // Header (64px) + tabs area + stats circles ≈ 230px from top
    const VIDEO_BLANKET_TOP = 60
    const SIDE_DRAWER_WIDTH = 400
    const SIDE_NAV_BAR_WIDTH = 60
    const videoBlanketRect = {
        anchorTop: VIDEO_BLANKET_TOP,
        anchorLeft: SIDE_NAV_BAR_WIDTH,
        height: window.innerHeight - VIDEO_BLANKET_TOP,
        width: window.innerWidth - SIDE_DRAWER_WIDTH - SIDE_NAV_BAR_WIDTH,
    }

    const {
        getUnitConfigMap,
        getInventoryConfigMap,
        getAreaConfigMap,
        unitConfigList,
        inventoryConfigList,
        customStatusList,
    } = useAptConfig({})
    const unitConfigMap = getUnitConfigMap()
    const inventoryConfigMap = getInventoryConfigMap()
    const areaConfigMap = getAreaConfigMap()
    const { setChannel } = useMessageDrawer()

    const inspection = useSelector((state: RootState) => state.inspection)

    const {
        inspectionDetail,
        unitInspectionMap,
        unitCompletionMap,
        fetchInspectionScore,
        inspectionScore,
        unitInspectionList,
    } = useInspection(Number(id) ?? -1, {
        getDetail: true,
    })
    const {
        filter,
        setUnitSearch,
        isInventoryInspectionInFilter,
    } = useInspectionFilter({ cleanUp: true })

    const nextLink = useSelector(
        (state: RootState) =>
            state.pagination.GET_UNIT_INSPECTION_LIST_REQUEST?.next,
    )

    const unitInsListLoading = useSelector(
        (state: RootState) =>
            state.inspection.isLoading[GET_UNIT_INSPECTION_LIST_REQUEST],
    )

    const [unitDamageMap, setUnitDamageMap] = useState<ModelListMap<Damage>>({})
    const [damageUnit, setDamageUnit] = useState<Unit | undefined>()

    const [actionItemList, setActionItemList] = useState<ActionItem[] | null>(
        null,
    )

    const [invInspectionList, setInvInspectionList] = useState<
        InventoryInspection[] | null
    >(null)

    const [messageDrawerTitle, setMessageDrawerTitle] = useState('')
    const [selectedUnit, setSelectedUnit] = useState<null | Unit>(null)

    const locationFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'inspection',
    })
    const assignmentFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.PrunedSelection,
        selectionMode: FinderSelectionMode.Multi,
        useCase: 'inspection',
    })
    const { tree, unitCount, unitList } = useRootInfrastructure(
        inspectionDetail !== undefined,
    )

    // Memoize unit map to avoid O(n) traversal on every click
    const unitMap = useMemo(() => {
        const map: Record<number, Unit> = {}
        traverse(tree.root, (folder) => {
            folder.units.forEach((unit) => {
                map[unit.id] = unit
            })
        })
        return map
    }, [tree.root])
    const prunedTree = _usePrunedInfrastructure(
        tree,
        locationFinderSelection.selection,
    )

    const { areaStatusTagMap } = useAreaStatusTag({
        getListForSchedule: inspectionDetail?.schedule?.id ?? -1,
        cleanUp: true,
    })

    const { userList, actions } = useUser()
    const { getUserList } = actions

    const { serviceList } = useService({})
    const { vendorList } = useCompany({})

    const { scheduleList, getScheduleList } = useSchedule()

    // Memoize filtered schedule list to avoid filtering on every render
    const activeScheduleList = useMemo(
        () => scheduleList.filter((schedule) => schedule.active),
        [scheduleList],
    )

    const loadSchedules = async () => {
        getScheduleList({})
    }

    const inspectionAnalytics = useSelector(
        (state: RootState) => state.inspection.inspectionAnalytics,
    )

    // Memoize inventoryConfigIDs to avoid recalculating on every render
    const inventoryConfigIDs = useMemo(() => {
        if (!inspectionAnalytics) return []
        return Object.keys(inspectionAnalytics.inventory_config_analytics)
            .filter((invConfIdStr) => invConfIdStr !== 'null')
            .map((invConfIdStr) => Number(invConfIdStr))
    }, [inspectionAnalytics])

    useEffect(() => {
        dispatch(
            getUnitInspectionList({
                inspection_id: Number(id),
                params: { limit: 100, offset: 0 },
            }),
        )
        dispatch(
            getInspectionAnalytics({
                inspection_id: Number(id),
            }),
        )
        // dispatch(getStatusGroupList({}))
        // dispatch(getStatusList({}))
        axiosInstance
            .get('inspection/inventory-inspection/', {
                params: {
                    inspection: id,
                    has_actionitem: true,
                },
            })
            .then((res) => {
                setInvInspectionList(res.data)
            })

        // getUserList({ params: { my_team: true } })

        // loadSchedules()
        fetchInspectionScore(Number(id))
    }, [])

    const atBottom = useOnScreen(atBottomRef)

    useEffect(() => {
        if (atBottom && nextLink) {
            dispatch(
                getUnitInspectionList({
                    url: nextLink,
                    inspection_id: Number(id),
                }),
            )
        }
    }, [atBottom])

    useEffect(() => {
        if (detailUnitInspection) {
            dispatch(
                getInventoryInspectionList({
                    params: {
                        inspection: Number(id),
                        units: `${detailUnitInspection.unit.id}`,
                    },
                }),
            )
        } else {
            dispatch(setInventoryInspectionList(undefined))
        }
    }, [detailUnitInspection])

    const { areaLeaseMap, createDamage, damageList } = _useLease({
        cleanUp: true,
        getDamageListForInspecitonId: Number(id) ?? -1,
        createAreaLeaseMap: true,
    })

    useEffect(() => {
        if (damageList) {
            let curDamageIdx = 0
            const mutableDamageMap: ModelListMap<Damage> = {}
            traverse(tree.root, (folder) => {
                folder.units.forEach((unit) => {
                    while (
                        damageList[curDamageIdx] &&
                        damageList[curDamageIdx].unit === unit.id
                    ) {
                        if (mutableDamageMap[unit.id] === undefined) {
                            mutableDamageMap[unit.id] = []
                        }
                        mutableDamageMap[unit.id]!.push(
                            damageList[curDamageIdx],
                        )
                        curDamageIdx += 1
                    }
                })
            })
            setUnitDamageMap(mutableDamageMap)
        }
    }, [damageList])

    // Memoize to avoid recreating on every render
    const getUnitAdornment = useCallback(
        (unit: Unit) => {
            const unitInspection: BaseUnitInspection | undefined =
                unitCompletionMap[unit.id]

            if (unitInspection) {
                return (
                    <CheckCircleIcon
                        fontSize="small"
                        color={
                            unitInspection.complete_status
                                ? 'primary'
                                : 'secondary'
                        }
                    />
                )
            }

            return null
        },
        [unitCompletionMap],
    )

    const { filterApplied, setAllApplied } = useFilterApplied(
        filter,
        locationFinderSelection.selection,
    )

    // Memoize getFilteredParams to avoid recreating on every render
    const getFilteredParams = useCallback(() => {
        // build filters for request

        const params: UnitInspectionParams = {}

        if (filter.hasDamages) {
            params.has_damage = true
        }

        if (filter.unitSearch !== '') {
            params.unit_name = filter.unitSearch
        }
        if (
            filter.inspectionCompletionFilter !== InspectionCompletionFilter.All
        ) {
            params.complete =
                filter.inspectionCompletionFilter ===
                InspectionCompletionFilter.Complete
        }
        if (Object.keys(filter.unitConfigFilter).length > 0) {
            params.unit_configs = Object.keys(filter.unitConfigFilter).join(',')
        }

        if (Object.keys(filter.invConfigFilter).length > 0) {
            params.inventory_configs = Object.keys(filter.invConfigFilter).join(
                ',',
            )
        }
        if (Object.keys(filter.customStatusFilter).length > 0) {
            params.statuses = Object.keys(filter.customStatusFilter).join(',')
        }

        if (filter.hasFlaggedItem) {
            params.has_flag = true
        }
        if (filter.handled !== InspectionHandledFilter.All) {
            params.handled = filter.handled === InspectionHandledFilter.Handled
        }
        if (filter.hasCostDriver) {
            params.cost_driver = true
        }
        if (filter.hasMessages) {
            params.has_message = true
        }
        if (filter.unreadFilter) {
            params.has_unread = true
        }

        if (locationFinderSelection.selection.unit.length > 0) {
            const units = Object.keys(locationFinderSelection.selection.unit)
                .filter(
                    (key) =>
                        key !== 'length' &&
                        locationFinderSelection.selection.unit[Number(key)] !==
                            undefined,
                )
                .join(',')
            if (units) params.units = units
        }
        if (locationFinderSelection.selection.folder.length > 0) {
            const folders = Object.keys(
                locationFinderSelection.selection.folder,
            )
                .filter(
                    (key) =>
                        key !== 'length' &&
                        locationFinderSelection.selection.folder[
                            Number(key)
                        ] !== undefined,
                )
                .join(',')
            if (folders) params.folders = folders
        }

        if (inspectionDetail) {
            params.inspection = inspectionDetail.id
        }

        return params
    }, [filter, locationFinderSelection.selection, inspectionDetail])

    // Memoize to avoid recreating on every render
    const makeInventoryInspectionAnalyticsRequest = useCallback(() => {
        const params: UnitInspectionParams = getFilteredParams()
        if (inspectionDetail) {
            params.inspection = inspectionDetail.id
        }
        if (invInsAnalyticsBlanketData.inventoryConfig) {
            params.inventory_configs = invInsAnalyticsBlanketData.inventoryConfig.id.toString()
        }
        if (invInsAnalyticsBlanketData.statusId) {
            params.statuses = invInsAnalyticsBlanketData.statusId
        }
        axiosInstance
            .get('inspection/inventory-inspection/get_analytics_list/', {
                params: params,
            })
            .then((res) => {
                setInventoryInspectionAnalyticList(res.data)
            })
    }, [getFilteredParams, inspectionDetail, invInsAnalyticsBlanketData])

    // Memoize to avoid recreating on every render
    const makeFilteredRequest = useCallback(() => {
        const params: UnitInspectionParams = getFilteredParams()

        const req: UnitInspecitonListAxiosReq = {
            inspection_id: Number(id),
            params: params,
        }
        dispatch(getUnitInspectionList(req)).then(() => setAllApplied())
        dispatch(getInspectionAnalytics(req))
        makeInventoryInspectionAnalyticsRequest()
    }, [
        id,
        getFilteredParams,
        dispatch,
        setAllApplied,
        makeInventoryInspectionAnalyticsRequest,
    ])

    // Memoize to maintain stable reference
    const updateListState = useCallback(
        <T extends IdentifiableObject>(
            stateSetter: React.Dispatch<React.SetStateAction<T[] | null>>,
            updatedModel: T,
        ) => {
            stateSetter((prevList) => {
                if (!prevList) return [updatedModel]
                return prevList.map((existingModel) =>
                    existingModel.id === updatedModel.id
                        ? updatedModel
                        : existingModel,
                )
            })
        },
        [],
    )

    // Memoize to maintain stable reference
    const onAddMessage = useCallback(
        <T extends IdentifiableObject>(
            modelList: T[],
            modelId: number,
            updateHandler: StateSetter<T>,
        ) => {
            const model = modelList.find((m) => m.id === modelId)
            if (model) {
                const newModel = { ...model } as any
                newModel.message_count = (newModel.message_count ?? 0) + 1
                updateListState(updateHandler, newModel)
            }
        },
        [updateListState],
    )

    const latestCompletedDateTime: Date | null = useMemo(() => {
        let latestDate: Date | null = null

        unitInspectionList?.forEach((unitInspection: UnitInspection) => {
            // Check if the inspection is complete and has a valid sort_date
            if (
                unitInspection.complete_status === 1 &&
                unitInspection.sort_date
            ) {
                const completedDate = new Date(unitInspection.sort_date)
                // Update latestDate if completedDate is more recent
                if (!latestDate || completedDate > latestDate) {
                    latestDate = completedDate
                }
            }
        })

        return latestDate
    }, [unitInspectionList])

    const openVideoInspectionModal = (unitInspection: UnitInspection) => {
        if (unitInspection.offline_inspection) {
            setOfflineInspectionModalOpen(true)
        } else {
            setVideoBlanketUnitInspection(unitInspection)
            setVideoBlanketOpen(true)
        }
    }

    // Keep the video blanket synced to whichever unit is currently selected.
    useEffect(() => {
        if (
            videoBlanketOpen &&
            detailUnitInspection &&
            videoBlanketUnitInspection?.id !== detailUnitInspection.id
        ) {
            setVideoBlanketUnitInspection(detailUnitInspection)
        }
    }, [videoBlanketOpen, detailUnitInspection, videoBlanketUnitInspection])

    return (
        <SideDrawerContainer open={true} width={400}>
            {/* Main Content */}
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    height: 'calc(100vh - 104px)',
                }}
            >
                {/* Header */}

                <Container
                    style={{
                        padding: theme.spacing(1),
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                    }}
                >
                    {/* Inspection Title and controls */}
                    <Container
                        style={{
                            width: '100%',
                            alignItems: 'center',
                            minWidth: 0,
                            flexWrap: 'wrap',
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h5),
                                fontWeight: 700,
                                marginRight: theme.spacing(1),
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {inspectionDetail?.name ?? ''}
                        </span>

                        <span style={{ flexShrink: 0 }}>
                            {inspectionScore.total} / {inspectionScore.max}
                            <span
                                style={{
                                    marginLeft: theme.spacing(1),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                (
                                {inspectionScore.max > 0
                                    ? Math.round(
                                          (inspectionScore.total /
                                              inspectionScore.max) *
                                              100,
                                      )
                                    : 0}
                                %)
                            </span>
                        </span>
                        <div style={{ flex: 1, minWidth: theme.spacing(1) }} />

                        <Container
                            onClick={handlePopoverClick}
                            style={{ flexShrink: 0 }}
                        >
                            <Tooltip title="Reports / Setup">
                                <IconButton onClick={() => {}}>
                                    <FileCopy fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </Container>

                        <ScheduleReportPopOver
                            open={popoverOpen}
                            onClose={handlePopoverClose}
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                horizontal: 'left',
                                vertical: 'bottom',
                            }}
                            reportItems={[
                                {
                                    title: 'Full Report',
                                    reportFileType: 'excel',
                                    reportDelivery: 'download',
                                    downloadLink: {
                                        title: 'Download',
                                        onClick: () => {
                                            const invConfigList = inventoryConfigIDs.join(
                                                ',',
                                            )
                                            const statusFilterKeys = Object.keys(
                                                filter.customStatusFilter,
                                            )
                                            axiosInstance
                                                .get(
                                                    'inspection/inventory-inspection/get_analytics_list/',
                                                    {
                                                        params: {
                                                            inspection:
                                                                inspection
                                                                    .inspectionDetail
                                                                    ?.id,
                                                            inventory_configs: invConfigList,
                                                            ...(statusFilterKeys.length >
                                                            0
                                                                ? {
                                                                      statuses: statusFilterKeys.join(
                                                                          ',',
                                                                      ),
                                                                  }
                                                                : {}),
                                                            excel: true,
                                                        },
                                                        responseType: 'blob',
                                                    },
                                                )
                                                .then((res) => {
                                                    FileDownload(
                                                        new Blob([res.data]),
                                                        'inventoryInspectionList.xlsx',
                                                    )
                                                })
                                        },
                                    },
                                },
                                {
                                    title: 'Filtered Full Property PDF',
                                    titleSubtitle: '(with messages)',
                                    reportFileType: 'pdf',
                                    reportDelivery: 'email',
                                    downloadLink: {
                                        title: 'Email report',
                                        onClick: () => {
                                            axiosInstance
                                                .get(
                                                    `inspection/${inspectionDetail?.id}/get_report/`,
                                                    {
                                                        params: getFilteredParams(),
                                                    },
                                                )
                                                .then(() => {
                                                    toast.success(
                                                        'Your report is generating. This report may take a few hours, you will recieve an email when it is complete',
                                                    )
                                                })
                                        },
                                    },
                                },
                                {
                                    title: 'Inventory Totals Report',
                                    reportFileType: 'excel',
                                    reportDelivery: 'email',
                                    downloadLink: {
                                        title: 'Email report',
                                        onClick: () => {
                                            axiosInstance.get(
                                                'inspection/inventory-inspection/create_report/',
                                                { params: getFilteredParams() },
                                            )
                                            toast.success(
                                                'You will receive an email with your report shortly!',
                                            )
                                        },
                                    },
                                },
                            ]}
                        />

                        <Container style={{ flexShrink: 0 }}>
                            <Tooltip title="Filter">
                                <IconButton
                                    onClick={() => {
                                        setFilterOpen(!filterOpen)
                                    }}
                                    color={filterOpen ? 'primary' : 'default'}
                                >
                                    <TuneIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Assign">
                                <IconButton
                                    onClick={() => {
                                        setAssignOpen(!assignOpen)
                                        setReassignOpen(false)
                                        setFilterOpen(false)
                                    }}
                                    color={assignOpen ? 'primary' : 'default'}
                                >
                                    <AddIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reassign">
                                <IconButton
                                    onClick={() => {
                                        setReassignOpen(!reassignOpen)
                                        setAssignOpen(false)
                                        setAssignModalOpen(false)
                                        setFilterOpen(false)
                                    }}
                                    color={reassignOpen ? 'primary' : 'default'}
                                >
                                    <GroupAddIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </Container>
                    </Container>

                    {/* Analytics */}
                    <InspectionAnalyticsHeadline totalUnitCount={unitCount} />
                </Container>

                {/* Content area that blankets will cover */}
                <div
                    ref={contentRef}
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    {latestCompletedDateTime && (
                        <Container style={{ padding: theme.spacing(1) }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    fontWeight: 700,
                                }}
                            >
                                Latest Completed Unit:{' '}
                                {toLocaleString(
                                    latestCompletedDateTime as Date,
                                )}
                            </span>
                        </Container>
                    )}

                    <InfrastructureFilterChips
                        filter={filter}
                        unitConfigList={unitConfigList}
                        locationSelection={locationFinderSelection.selection}
                        setLocationSelection={
                            locationFinderSelection.setFinderSelection
                        }
                        invConfigList={inventoryConfigList}
                        customStatusList={customStatusList}
                        filterApplied={filterApplied}
                        makeFilteredRequest={makeFilteredRequest}
                    />
                    {/* End Header */}

                    <Divider />

                    <div
                        style={{
                            flex: 1,
                            overflow: 'auto',
                        }}
                    >
                        <InspectionAnalytics
                            setInvConfAnalyticsBlanketData={
                                setInvInsAnalyticsBlanketData
                            }
                            setInvConfAnalyticsBlanketOpen={
                                setInvInsAnalyitcsBlanketOpen
                            }
                        />
                    </div>
                </div>
            </Container>
            {/* End Main Content */}

            {/* Unit Inspection Side Drawer */}
            <UnitInspectionSideDrawer
                open={true}
                inspectionId={Number(id)}
                unitInspectionList={unitInspectionList ?? []}
                unitInspectionMap={unitInspectionMap}
                onClose={() => {}}
                selectedUnitInspectionId={detailUnitInspection?.id}
                searchText={filter.unitSearch}
                unitDamageMap={unitDamageMap}
                onSearchChange={setUnitSearch}
                onApplySearch={makeFilteredRequest}
                onClickUnitInspection={(unitInspection) => {
                    if (detailUnitInspection?.id === unitInspection.id) {
                        setDetailUnitInspection(undefined)
                    } else {
                        setDetailUnitInspection(unitInspection)
                    }
                }}
                onClickDamage={(unitInspection) => {
                    // Use memoized unitMap for O(1) lookup instead of traversal
                    const foundUnit = unitMap[unitInspection.unit.id]
                    if (foundUnit) {
                        setDamageUnit(foundUnit)
                    }
                }}
                onClickUnitNotes={(unitInspection) => {
                    // Use memoized unitMap for O(1) lookup instead of traversal
                    const foundUnit = unitMap[unitInspection.unit.id]
                    if (foundUnit?.channel_id) {
                        setDetailUnitInspection(undefined)
                        setChannel(foundUnit.channel_id)
                        setMessageDrawerOpen(true)
                        setMessageDrawerTitle(`Unit Notes ${foundUnit.name}`)
                        setSelectedUnit(foundUnit)
                    }
                }}
                onClickVideo={(unitInspection) => {
                    setVideoBlanketUnitInspection(unitInspection)
                    setVideoBlanketOpen(true)
                }}
            />

            {/* Assign Drawer */}
            <SideDrawer
                open={assignOpen}
                handleClose={() => setAssignOpen(false)}
                title="Assign"
                width={400}
            >
                <AssignInspectionForm
                    prunedTree={prunedTree.root}
                    onClose={() => setAssignOpen(false)}
                    setAssignModalOpen={() => setAssignModalOpen(true)}
                    getUnitAdornment={getUnitAdornment}
                />
            </SideDrawer>

            {/* Filter Drawer */}
            <FilterForm
                open={filterOpen}
                unitConfigList={unitConfigList}
                inventoryConfigList={inventoryConfigList ?? []}
                customStatusList={customStatusList}
                tree={tree.root}
                finderSelection={locationFinderSelection}
                handleClose={() => setFilterOpen(false)}
                makeFilteredRequest={makeFilteredRequest}
            />

            <DamageDrawer
                unit={damageUnit}
                openMessages={(damage: Damage) => {
                    setChannel(damage.channel)
                    setMessageDrawerOpen(true)
                }}
                handleClose={() => setDamageUnit(undefined)}
                areaConfigMap={areaConfigMap}
                createDamage={createDamage}
                unitDamages={unitDamageMap[damageUnit?.id ?? -1]}
                inspection={inspectionDetail?.id}
                width={400}
            />

            <MessageDrawer
                title={messageDrawerTitle}
                open={messageDrawerOpen}
                onClose={() => {
                    setActiveChannel({ modelId: null, type: null })
                    setMessageDrawerOpen(false)
                    setCurrentInvInsAnalytic(undefined)
                    setMessageDrawerTitle('')
                    setSelectedUnit(null)
                }}
                onCreateMessage={() => {
                    if (currentInvInsAnalytic) {
                        setCurrentInvInsAnalytic({
                            ...currentInvInsAnalytic,
                            unread_count: 0,
                            channel_message_count:
                                currentInvInsAnalytic.channel_message_count + 1,
                        })
                    }

                    if (
                        activeChannel.modelId !== null &&
                        activeChannel.type !== null
                    ) {
                        if (activeChannel.type === 'action-item') {
                            onAddMessage(
                                actionItemList ?? [],
                                activeChannel.modelId,
                                setActionItemList,
                            )
                        }

                        if (activeChannel.type === 'inventory-inspection') {
                            onAddMessage(
                                invInspectionList ?? [],
                                activeChannel.modelId,
                                setInvInspectionList,
                            )
                        }
                    }

                    if (selectedUnit) {
                        const newUnitList = unitList?.map((unit) => {
                            if (unit.id === selectedUnit.id) {
                                return {
                                    ...unit,
                                    unit_notes_msg_count:
                                        unit.unit_notes_msg_count + 1,
                                }
                            }
                            return unit
                        })
                        dispatch(setUnits(newUnitList ?? []))
                    }
                }}
                width={400}
            />

            <InspectionAssignModal
                makeFilteredRequest={makeFilteredRequest}
                open={assignModalOpen}
                areaConfigMap={areaConfigMap}
                onClose={() => setAssignModalOpen(false)}
                inspectionDetail={inspectionDetail}
                unitCompletionMap={unitCompletionMap}
                areaStatusMap={areaStatusTagMap}
            />

            <ReassignInspectionModal
                open={reassignOpen}
                inspectionId={Number(id)}
                root={tree.root}
                unitCompletionMap={unitCompletionMap}
                onClose={() => setReassignOpen(false)}
                makeFilteredRequest={makeFilteredRequest}
            />

            <UnitInspectionDetailForm
                unitConfigMap={unitConfigMap}
                inventoryConfigMap={inventoryConfigMap}
                unitInspection={detailUnitInspection}
                areaStatusMap={areaStatusTagMap}
                open={detailUnitInspection !== undefined}
                isInventoryInspectionInFilter={isInventoryInspectionInFilter}
                onClose={() => setDetailUnitInspection(undefined)}
                setUnitInspection={setDetailUnitInspection}
            />

            <InvInsAnalyticsBlanket
                setOpen={setInvInsAnalyitcsBlanketOpen}
                open={invInsAnalyitcsBlanketOpen}
                currentInvInsAnalytic={currentInvInsAnalytic}
                setCurrentInvInsAnalytic={setCurrentInvInsAnalytic}
                setInventoryInspectionAnalyticList={
                    setInventoryInspectionAnalyticList
                }
                inventoryInspectionAnalyticList={
                    inventoryInspectionAnalyticList
                }
                makeInventoryInspectionAnalyticRequest={
                    makeInventoryInspectionAnalyticsRequest
                }
                blanketData={invInsAnalyticsBlanketData}
                getFilteredParams={getFilteredParams}
                inspection={inspectionDetail}
                rect={analyticRect}
                openMessages={(channelId: number) => {
                    setChannel(channelId)
                    setMessageDrawerOpen(true)
                }}
                userList={userList}
                serviceList={serviceList}
                vendorList={vendorList}
                scheduleList={activeScheduleList}
            />

            {videoBlanketUnitInspection && (
                <VideoInspectionBlanket
                    open={videoBlanketOpen}
                    onClose={() => setVideoBlanketOpen(false)}
                    rect={videoBlanketRect}
                    unitInspectionVideoIds={
                        videoBlanketUnitInspection?.video_ids ?? []
                    }
                    unitInspection={videoBlanketUnitInspection}
                />
            )}
        </SideDrawerContainer>
    )
}

export type ActiveChannelModel = 'action-item' | 'inventory-inspection' | null

export type ActiveChannel = {
    type: ActiveChannelModel
    modelId: number | null
}

type StateSetter<T extends IdentifiableObject> = React.Dispatch<
    React.SetStateAction<T[] | null>
>
